import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { getAllCachedProducts } from "../../services/posProductCache";
import { findProductSuggestions } from "../../utils/productSuggestions";

export default function CachedProductSearch({
  cacheScope, cacheVersion, value, onChange, onClear, onSubmit, onSelect,
  isSearching = false, isGrabFoodyMode = false,
}) {
  const [catalog, setCatalog] = useState({ scope: null, products: [] });
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const optionRefs = useRef([]);
  const listId = useId();

  useEffect(() => {
    let active = true;
    getAllCachedProducts(cacheScope).then((products) => {
      if (active) setCatalog({ scope: cacheScope, products });
    }).catch((error) => {
      if (active) setCatalog({ scope: cacheScope, products: [] });
      console.warn("Không đọc được cache để gợi ý sản phẩm:", error.message);
    });
    return () => { active = false; };
  }, [cacheScope, cacheVersion]);

  const suggestions = useMemo(() => findProductSuggestions(
    // Same ranking as the desktop POS: name start, initials, words, then code.
    catalog.scope === cacheScope ? catalog.products : [], value, 8, { desktop: true },
  ), [catalog, cacheScope, value]);
  const expanded = isOpen && !isSearching && suggestions.length > 0;

  useEffect(() => { setActiveIndex(-1); }, [value, catalog, cacheScope]);
  useEffect(() => {
    if (expanded && activeIndex >= 0) optionRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, expanded]);

  const selectProduct = (product) => {
    onSelect(product);
    inputRef.current?.blur();
    setIsOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (event.nativeEvent.isComposing) return;
    if ((event.key === "ArrowDown" || event.key === "ArrowUp") && suggestions.length) {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(true);
      setActiveIndex((current) => event.key === "ArrowDown"
        ? (current + 1) % suggestions.length
        : current <= 0 ? suggestions.length - 1 : current - 1);
    } else if (event.key === "Enter" && expanded && activeIndex >= 0) {
      event.preventDefault();
      event.stopPropagation();
      selectProduct(suggestions[activeIndex]);
    } else if (event.key === "Escape") {
      event.stopPropagation();
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  return (
    <form className="search-form cached-product-search" onSubmit={(event) => {
      setIsOpen(false);
      onSubmit(event);
    }} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
    }}>
      <div className="search-input-group">
        <div className="search-icon-left"><i className="fas fa-search" /></div>
        <input
          ref={inputRef} type="text" className="search-input"
          placeholder="Tìm tên/mã vạch hoặc LoSX dạng lo237..."
          aria-label="Tìm sản phẩm" role="combobox" aria-autocomplete="list"
          aria-expanded={expanded} aria-controls={listId}
          aria-activedescendant={expanded && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          autoComplete="off" value={value} disabled={isSearching}
          onChange={(event) => { onChange(event.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown}
        />
        {value && <button type="button" className="search-clear-btn" title="Xóa tìm kiếm" aria-label="Xóa tìm kiếm" onClick={() => {
          onClear(); inputRef.current?.focus(); setIsOpen(false); setActiveIndex(-1);
        }}><i className="fas fa-times" aria-hidden="true" /></button>}
        <button className="search-submit-btn" type="submit" disabled={isSearching || !value.trim()}>
          {isSearching ? <div className="search-spinner"><i className="fas fa-spinner fa-spin" /></div> : <span>Tìm kiếm</span>}
        </button>
      </div>
      {expanded && (
        <ul id={listId} className="product-suggestions" role="listbox" aria-label="Sản phẩm gợi ý">
          {suggestions.map((product, index) => (
            <li key={`${product.id}:${product.unit || ""}`} role="presentation">
              <button
                ref={(element) => { optionRefs.current[index] = element; }}
                id={`${listId}-${index}`} type="button" role="option"
                aria-selected={index === activeIndex} tabIndex={-1}
                className={`product-suggestion${index === activeIndex ? " active" : ""}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectProduct(product)}
              >
                <span className="product-suggestion-info">
                  <strong>{product.name}</strong>
                  <small>{product.id}{product.categoryName ? ` · ${product.categoryName}` : ""}{product.unit ? ` · ${product.unit}` : ""}</small>
                </span>
                <span className="product-suggestion-price">
                  {(Number(isGrabFoodyMode ? product.price2 ?? product.price : product.price) || 0).toLocaleString("vi-VN")}₫
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </form>
  );
}
